// frontend/src/features/orders/components/Pedidos/OrderDetailModal.tsx
import React, { useState, useEffect } from 'react';
import { Order, FullOrderDetails, fetchOrderDetails, closeOrder, updateOrderOperacao, updateOrderGrade, GradeItemPayload, addItemsToOrder } from '../../services/ordersAPI';
import styles from './OrderDetailModal.module.css';
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner';
import ProductSearchInput from './ProductSearchInput';
import api from '../../../../services/apiConfig';
import { openOrderPrint } from './OrderPrintView';
import { useThemeByEmpresa } from '../../../../hooks/useThemeByEmpresa';
import { useAuthStore } from '../../../../stores/authStore';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery } from '@tanstack/react-query';
import { useFilterStore } from '../../../../stores/filterStore';
import { fetchSalesData, fetchGoalsData, fetchSupervisorGoalsData } from '../../../dashboard/services/dashboardAPI';

interface OrderDetailModalProps {
    order: Order;
    onClose: () => void;
    onOrderUpdated?: () => void;
}

const formatDateForInput = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
        return dateString.split('T')[0];
    } catch (e) {
        return '';
    }
};

// FUNÇÃO HELPER PARA FORMATAR MOEDA COM SEGURANÇA
const formatCurrency = (value: any) => {
    if (value === null || value === undefined || value === '') return 'R$ 0,00';
    const num = Number(value);
    if (isNaN(num)) return 'R$ 0,00';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Helper para formatar decimais (ex: comissão)
const formatDecimal = (value: any, decimals: number = 3) => {
    if (value === null || value === undefined || value === '') return Number(0).toFixed(decimals);
    const num = Number(value);
    return isNaN(num) ? Number(0).toFixed(decimals) : num.toFixed(decimals);
};

type OrderTab = 'entrega' | 'itens' | 'obs' | 'historico' | 'cotacoes_fretes' | 'relacionamento' | 'agendamento';

interface PedidoFormData extends Partial<FullOrderDetails['pedido']> {
    OBS?: string;
    ENTREGA_NUMERO?: string;
    NOVO_AGENDAMENTO_DATA?: string;
    NOVO_AGENDAMENTO_OBS?: string;
    MC_TOTAL?: number;
    VENDEDOR_NOME?: string;
    CLIENTE_SEGMENTO_NOME?: string;
    COND_PAG_DESCRICAO?: string;
    OPERACAO_FISCAL?: number;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ order, onClose, onOrderUpdated }) => {
    const { empresa } = useThemeByEmpresa();
    const isImbiseg = empresa === 'IMBISEG';
    const { user } = useAuthStore();
    const isSupervisor = user?.role === 'supervisor' || user?.role === 'admin' || user?.role === 'diretor';
    
    const [details, setDetails] = useState<FullOrderDetails | null>(null);

    // ======== LÓGICA DE SIMULAÇÃO DE META ========
    const { initialDate, finalDate, selectedCarteiraId, activePreset } = useFilterStore();
    const userBase = user?.base;

    const { data: salesResponse } = useQuery({
        queryKey: ['salesData', initialDate, finalDate, selectedCarteiraId, activePreset, userBase],
        queryFn: async () => {
            if (!initialDate || !finalDate) return null;
            const response = await fetchSalesData(initialDate, finalDate, selectedCarteiraId, activePreset || undefined);
            return response.data as any;
        },
        enabled: !!initialDate && !!finalDate,
        staleTime: 5 * 60 * 1000,
    });

    const { data: goalsResponse } = useQuery({
        queryKey: ['goalsData', initialDate, finalDate, selectedCarteiraId, isSupervisor, userBase],
        queryFn: async () => {
            if (!initialDate || !finalDate) return [];
            if (isSupervisor && !selectedCarteiraId) {
                const res = await fetchSupervisorGoalsData(initialDate, finalDate);
                return res.data.goals as any[];
            } else if (isSupervisor && selectedCarteiraId) {
                const res = await fetchGoalsData(initialDate, finalDate, selectedCarteiraId);
                return res.data.goals as any[];
            } else {
                const targetBaseId = userBase?.toString();
                if (!targetBaseId) return [] as any[];
                const res = await fetchGoalsData(initialDate, finalDate, targetBaseId);
                return res.data.goals as any[];
            }
        },
        enabled: !!initialDate && !!finalDate,
        staleTime: 5 * 60 * 1000,
    });

    const { currentScore, simulatedScore } = React.useMemo(() => {
        if (!salesResponse?.salesData || !goalsResponse) return { currentScore: null, simulatedScore: null };
        
        const globalSales = salesResponse.salesData.reduce((acc: number, curr: any) => acc + (Number(curr.Venda) || 0), 0);
        const globalMargin = salesResponse.salesData.reduce((acc: number, curr: any) => acc + (Number(curr.Margem) || 0), 0);
        
        const currentGoalData = Array.isArray(goalsResponse) ? goalsResponse[0] : goalsResponse;
        const targetSales = Number(currentGoalData?.goal) || 0;
        const targetMargin = Number(currentGoalData?.marginGoal) || 0;
        const scoreFinal = currentGoalData?.scoreFinal || 0;
        
        if (targetSales === 0) return { currentScore: scoreFinal, simulatedScore: scoreFinal };
        
        const parseNumber = (val: any) => {
            if (val === null || val === undefined || val === '') return 0;
            if (typeof val === 'number') return isNaN(val) ? 0 : val;
            if (typeof val === 'string') {
                let clean = val.replace(/[^0-9.,-]/g, '');
                if (clean.includes(',') && clean.includes('.')) {
                    clean = clean.replace(/\./g, '').replace(',', '.');
                } else if (clean.includes(',')) {
                    clean = clean.replace(',', '.');
                }
                const parsed = Number(clean);
                return isNaN(parsed) ? 0 : parsed;
            }
            return Number(val) || 0;
        };
        const valorProdutosSim = parseNumber(details?.resumo?.TotalValorProdutos);
        const margemReaisSim = parseNumber(details?.resumo?.MC_TOTAL);
        
        const newSales = globalSales + valorProdutosSim;
        const newMargin = globalMargin + margemReaisSim;
        
        let scoreBase = 0;
        if (targetMargin > 0) {
            scoreBase = (Math.min(globalSales / targetSales, 2) * 0.5 + Math.min(globalMargin / targetMargin, 2) * 0.5) * 100;
        } else {
            scoreBase = Math.min(globalSales / targetSales, 2) * 100;
        }
        
        let scoreComSim = 0;
        if (targetMargin > 0) {
            scoreComSim = (Math.min(newSales / targetSales, 2) * 0.5 + Math.min(newMargin / targetMargin, 2) * 0.5) * 100;
        } else {
            scoreComSim = Math.min(newSales / targetSales, 2) * 100;
        }
        
        const delta = scoreComSim - scoreBase;
        const current = scoreFinal > 0 ? scoreFinal : scoreBase;
        const simulated = Math.min(current + delta, 200);
        
        return { currentScore: current, simulatedScore: simulated };
    }, [salesResponse, goalsResponse, details]);
    // ===============================================
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isProcessingSupervisor, setIsProcessingSupervisor] = useState(false);

    const [activeTab, setActiveTab] = useState<OrderTab>('itens');
    const [formData, setFormData] = useState<PedidoFormData>({});

    const [isEditing, setIsEditing] = useState(false);
    const [isClientExpanded, setIsClientExpanded] = useState(false);
    const [isSavingOperacao, setIsSavingOperacao] = useState(false);
    const [isSavingObs, setIsSavingObs] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [showCotacaoAlert, setShowCotacaoAlert] = useState(false);
    const [isClosingOrder, setIsClosingOrder] = useState(false);


    // Estados para o dropdown de operação buscável
    const [isOpSelectOpen, setIsOpSelectOpen] = useState(false);
    const [opSearchTerm, setOpSearchTerm] = useState('');

    // Estados para o sub-modal de grade/itens unificado
    const [showGradeModal, setShowGradeModal] = useState(false);
    const [isSavingGrade, setIsSavingGrade] = useState(false);

    // Lista unificada de itens (existentes + novos)
    const [pendingItems, setPendingItems] = useState<any[]>([]);

    // Item em edição no form (pode ser novo ou existente)
    const [editingItem, setEditingItem] = useState<any>(null);
    const [editingPendingIdx, setEditingPendingIdx] = useState<number | null>(null);

    const [productSearchKey, setProductSearchKey] = useState(0);
    const [tabelasList, setTabelasList] = useState<any[]>([]);
    const [optionsList, setOptionsList] = useState<any[]>([]);

    const availableSizes = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];

    // Reset formulário para um novo item vazio
    const resetEditingItem = () => {
        const initSizes: { [k: number]: number } = {};
        availableSizes.forEach(s => initSizes[s] = 0);
        setEditingItem({
            id: `new_${Date.now()}`,
            produto_id: 0,
            referencia: '',
            descricao: '',
            linha: '',
            grupo2: '',
            cod_pcp: '',
            base_cost: 0,
            mc_rate: 0,
            option_cost: 0,
            opcionais: '',
            preco: 0,
            tabela: '',
            comissao: 0,
            com_imb: 0,
            unidade: 'Pares',
            sizes: initSizes,
            quantidade: 0,
            valor_total: 0
        });
        setEditingPendingIdx(null);
        setProductSearchKey(k => k + 1);
    };

    // Carrega tabelas e opcionais quando abre o modal
    useEffect(() => {
        if (!showGradeModal) return;
        const fetchLists = async () => {
            try {
                const [tabRes, optRes] = await Promise.all([
                    api.get('/orders/tabelas'),
                    api.get('/orders/options')
                ]);
                setTabelasList(tabRes.data || []);
                setOptionsList(optRes.data || []);
            } catch (e) { console.error('Erro ao carregar listas:', e); }
        };
        fetchLists();
    }, [showGradeModal]);

    const handleOpenGradeModal = () => {
        if (!details || !details.items) return;
        const mappedItems = details.items.map((item: any) => {
            const sizes: { [key: number]: number } = {};
            availableSizes.forEach(s => { sizes[s] = Number(item[s]) || 0; });
            return {
                id: item.ID, vendaId: item.VENDAID || order.ID,
                tabela: item.TABELA || '', comissao: item.COMISSAO || 0,
                com_imb: item.COM_IMBSEG || 0, unidade: item.UNIDADE || 'Pares',
                cod_pcp: item.COD_PCP || '', referencia: item.REFERENCIA || '',
                descricao: item.DESCRICAO || '', opcionais: item.OPCIONAIS || '',
                preco: Number(item.PRECO) || 0, quantidade: Number(item.QUANTIDADE) || 0,
                valor_total: Number(item.VALOR_TOTAL) || 0, mc_rate: 0, sizes,
                isExisting: true
            };
        });
        setPendingItems(mappedItems);
        resetEditingItem();
        setShowGradeModal(true);
    };

    // --- CÁLCULO DE PREÇO (REPLICADO DO CREATEORDERMODAL) ---
    const calculateFinalSellingPrice = (baseCost: number, optionCost: number, mcRate: number, comissao: number, linha?: string, precoImbiseg?: number | null, overrideFin?: number, overridePdd?: number, qty: number = 1, pricelist?: any[], com_imb?: number, grupo2?: string) => {
        const pedidoData = details?.pedido as any;
        const icms = Number(pedidoData?.ICMS) || 0;
        const pisCofins = Number(pedidoData?.PIS_COFINS) || 0;
        const cprb = Number(pedidoData?.CPRB) || 0;
        const frete = Number(pedidoData?.MEDIA_FRETE) || 0;
        const segmentoId = pedidoData?.CLIENTE_SEGMENTO_ID;

        const isExportClient = segmentoId === 10;
        const isImbisegProduct = ((grupo2 || '').toUpperCase() === 'IMBISEG' || (linha || '').toUpperCase().includes('IMBISEG')) && !isExportClient;

        const pdd = isImbisegProduct ? 0 : (overridePdd !== undefined ? overridePdd : (Number(pedidoData?.PDD) || 0));
        const fin = overrideFin !== undefined ? overrideFin : (Number(pedidoData?.INDICE_FIN) || 0);
        const mc = Number(mcRate) || 0;
        let com = Number(comissao) || 0;

        if (isImbisegProduct) {
            let precoBase = 0;
            const lookupQty = qty > 0 ? qty : 1;
            if (pricelist && pricelist.length > 0) {
                const targetFaixaDecimal = (com_imb || 0) / 100;
                const faixaEntries = pricelist.filter(p => Math.abs(parseFloat(String(p.comissao)) - targetFaixaDecimal) < 0.0001);
                if (faixaEntries.length > 0) {
                    const tier = faixaEntries.find(p => lookupQty >= p.qtde_minima && lookupQty <= p.qtde_maxima) || faixaEntries[faixaEntries.length - 1];
                    precoBase = parseFloat(String(tier.preço));
                } else {
                    precoBase = parseFloat(String(pricelist[0].preço));
                }
            } else if (precoImbiseg) {
                precoBase = precoImbiseg;
            }

            const northRegionStates = ['RO', 'AC', 'AM', 'RR', 'PA', 'AP', 'TO'];
            const currentState = pedidoData?.CLIENTE_UF || '';
            const freightTypeId = Number(pedidoData?.FRETE_TIPO);

            if (northRegionStates.includes(currentState.toUpperCase()) && freightTypeId !== 3) {
                precoBase = precoBase * 1.07;
            }

            let finalPrice = (precoBase * (1 + (fin / 100))) + optionCost;
            const targetCom = 3;
            const comDiff = Math.round(com) - targetCom;
            if (comDiff > 0) {
                for (let i = 0; i < comDiff; i++) finalPrice = finalPrice * 1.03;
            } else if (comDiff < 0) {
                for (let i = 0; i < Math.abs(comDiff); i++) finalPrice = finalPrice / 1.03;
            }

            finalPrice = parseFloat(finalPrice.toFixed(2));
            return isNaN(finalPrice) ? 0 : finalPrice;
        }

        const fCom1 = com > 3 ? 3 : com;
        const totalDeductions = icms + pisCofins + cprb + fCom1 + frete + fin + mc + pdd;
        const divisor = 1 - (totalDeductions / 100);
        const fMarkup = divisor > 0 ? 1 / divisor : 0;

        let price = 0;
        if (fMarkup > 0) {
            price = (baseCost + optionCost) * fMarkup;
        }

        if (comissao > 3) {
            const extraSteps = Math.round(comissao) - 3;
            for (let i = 0; i < extraSteps; i++) {
                price = price * 1.03;
            }
        }

        return isNaN(price) ? 0 : price;
    };

    // --- Handlers do Sub-Modal Unificado ---
    const handleProductSelectInModal = async (product: any) => {
        if (!editingItem) return;
        const baseCost = parseFloat(product.CUSTO) || 0;
        const mcRate = parseFloat(product.MC) || 0;
        const linha = product.LINHA || '';
        const grupo2 = product.GRUPO2 || '';
        const precoImbiseg = product.PRECO_IMBISEG ? parseFloat(String(product.PRECO_IMBISEG)) : null;

        let pricelist: any[] = [];
        const isImbiseg = grupo2.toUpperCase() === 'IMBISEG' || linha.toUpperCase().includes('IMBISEG');

        if (isImbiseg) {
            try {
                const res = await api.get(`/products/imbiseg-prices/${product.REFERENCIA}`);
                pricelist = res.data;
            } catch (err) {
                console.error("Erro ao carregar tabela IMBISEG:", err);
            }
        }

        const defaultTable = tabelasList.find(t => t.ID === 1) || tabelasList[0];
        const comissao = defaultTable ? Number(defaultTable.COM) : 0;
        const tabela = defaultTable ? defaultTable.TABELA : '';

        let initialComImb = 0;
        if (isImbiseg && pricelist.length > 0) {
            const uniqueFaixas = Array.from(new Set(pricelist.map(p => parseFloat(String(p.comissao)) * 100)));
            uniqueFaixas.sort((a, b) => a - b);
            initialComImb = uniqueFaixas[0] || 0;
        }

        const price = calculateFinalSellingPrice(baseCost, 0, mcRate, comissao, linha, precoImbiseg, undefined, undefined, 1, pricelist, initialComImb, grupo2);

        setEditingItem({
            ...editingItem,
            produto_id: product.ID || 0, referencia: product.REFERENCIA,
            descricao: product.DESCRICAO, linha, grupo2, cod_pcp: product.COD_PCP || '',
            base_cost: baseCost, mc_rate: mcRate, preco_imbiseg: precoImbiseg,
            imbiseg_pricelist: pricelist, tabela, comissao, com_imb: initialComImb,
            preco: price, option_cost: 0
        });
    };

    // --- Handlers para EDITAR GRADE ---
    const handleSizeChangeInModal = (size: number, value: number) => {
        if (!editingItem) return;
        const newSizes = { ...editingItem.sizes, [size]: value };
        const newQty = Object.values(newSizes).reduce((acc: number, val: any) => acc + (val || 0), 0);
        
        let newPrice = editingItem.preco;
        // Recalculate price if it's IMBISEG because qty affects the price tier
        const isImbiseg = ((editingItem.grupo2 || '').toUpperCase() === 'IMBISEG' || (editingItem.linha || '').toUpperCase().includes('IMBISEG'));
        if (isImbiseg) {
             newPrice = calculateFinalSellingPrice(
                 editingItem.base_cost || 0,
                 editingItem.option_cost || 0,
                 editingItem.mc_rate || 0,
                 editingItem.comissao || 0,
                 editingItem.linha,
                 editingItem.preco_imbiseg,
                 undefined, undefined,
                 newQty,
                 editingItem.imbiseg_pricelist,
                 editingItem.com_imb,
                 editingItem.grupo2
             );
        }

        setEditingItem({ ...editingItem, sizes: newSizes, quantidade: newQty, preco: newPrice, valor_total: newQty * (newPrice || 0) });
    };

    const handleEditingItemChange = (field: string, value: any) => {
        if (!editingItem) return;
        let updated = { ...editingItem, [field]: value };
        
        let shouldRecalcPrice = false;
        if (field === 'tabela') {
            const t = tabelasList.find(x => x.TABELA === value);
            if (t) {
                updated.comissao = t.COM || 0;
                shouldRecalcPrice = true;
            }
        }
        if (field === 'opcionais') {
            const o = optionsList.find(x => x.OPCIONAL === value);
            updated.option_cost = o ? parseFloat(o.CUSTO) || 0 : 0;
            shouldRecalcPrice = true;
        }

        if (shouldRecalcPrice) {
             updated.preco = calculateFinalSellingPrice(
                 updated.base_cost || 0,
                 updated.option_cost || 0,
                 updated.mc_rate || 0,
                 updated.comissao || 0,
                 updated.linha,
                 updated.preco_imbiseg,
                 undefined, undefined,
                 updated.quantidade || 1,
                 updated.imbiseg_pricelist,
                 updated.com_imb,
                 updated.grupo2
             );
        }

        updated.valor_total = (updated.quantidade || 0) * (updated.preco || 0);
        setEditingItem(updated);
    };

    const handleSaveCurrentGrade = () => {
        if (!editingItem || !editingItem.referencia || editingItem.quantidade === 0) {
            alert('Selecione um produto e preencha a grade.'); return;
        }

        let newPending = [...pendingItems];
        if (editingPendingIdx !== null) {
            newPending[editingPendingIdx] = { ...editingItem };
        } else {
            newPending.push({ ...editingItem });
        }
        setPendingItems(newPending);
        resetEditingItem();
    };

    const handleConfirmAllChanges = async () => {
        // Se houver algo em edição com quantidade > 0, sugere salvar
        if (editingItem && editingItem.referencia && editingItem.quantidade > 0 && editingPendingIdx === null) {
            alert("Você tem uma grade preenchida que não foi adicionada. Clique em 'Salvar esta grade' primeiro.");
            return;
        }

        if (pendingItems.length === 0) { alert('Nenhum item adicionado.'); return; }

        setIsSavingGrade(true);
        try {
            // 1. Separar itens existentes (para update) dos novos (para insert)
            const existingItems = pendingItems.filter(i => i.isExisting);
            const newAddedItems = pendingItems.filter(i => !i.isExisting);

            // Update de existentes (via PATCH /api/orders/:id/grade)
            const gradePayload: GradeItemPayload[] = existingItems.map(pi => ({ itemId: pi.id, grade: pi.sizes }));
            if (gradePayload.length > 0) {
                await updateOrderGrade(order.ID, gradePayload);
            }

            // Insert de novos (via POST /api/orders/:id/items)
            if (newAddedItems.length > 0) {
                await addItemsToOrder(order.ID, newAddedItems.map(item => ({
                    produto_id: item.produto_id || 0, referencia: item.referencia, descricao: item.descricao,
                    tabela: item.tabela || '', comissao: item.comissao || 0, com_imb: item.com_imb || 0,
                    preco: item.preco || 0, base_cost: item.base_cost || 0, opcionais: item.opcionais || '',
                    cod_pcp: item.cod_pcp || '', unidade: item.unidade || 'Pares', sizes: item.sizes
                })));
            }

            // Recarregar os detalhes para atualizar a UI principal
            const refreshed = await fetchOrderDetails(order.ID);
            if (refreshed.data) { setDetails(refreshed.data); setFormData(refreshed.data.pedido || {}); }
            setShowGradeModal(false);
        } catch (err: any) {
            alert('Erro ao salvar: ' + (err?.response?.data?.error || err.message));
        } finally { setIsSavingGrade(false); }
    };


    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (showGradeModal) {
                    setShowGradeModal(false);
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, showGradeModal]);

    useEffect(() => {
        const loadDetails = async () => {
            try {
                setLoading(true);
                const response = await fetchOrderDetails(order.ID);

                if (response.data) {
                    setDetails(response.data);
                    setFormData(response.data.pedido || {});
                } else {
                    throw new Error("Resposta da API vazia.");
                }
                setError(null);
            } catch (err) {
                console.error(err);
                setError('Falha ao carregar detalhes do pedido.');
            } finally {
                setLoading(false);
            }
        };
        loadDetails();
    }, [order.ID]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = () => {
        console.log("Salvando dados:", formData);
        setIsEditing(false);
    };

    const [supervisorModalState, setSupervisorModalState] = useState<{
        isOpen: boolean;
        type: 'approve' | 'reject' | 'alert';
        status: 'success' | 'error' | 'warning';
        title: string;
        message: string;
    } | null>(null);

    const handleApproveOrderClick = () => {
        setSupervisorModalState({
            isOpen: true,
            type: 'approve',
            status: 'warning',
            title: 'Aprovar Pedido',
            message: 'Tem certeza que deseja aprovar este pedido?'
        });
    };

    const handleRejectOrderClick = () => {
        setSupervisorModalState({
            isOpen: true,
            type: 'reject',
            status: 'warning',
            title: 'Recusar Pedido',
            message: 'Tem certeza que deseja recusar este pedido e devolvê-lo ao vendedor?'
        });
    };

    const confirmSupervisorAction = async () => {
        if (!details?.pedido?.ID || !supervisorModalState) return;
        const action = supervisorModalState.type;
        setIsProcessingSupervisor(true);
        try {
            await api.put(`/orders/${details.pedido.ID}/supervisor-status`, { action });
            if (action === 'approve') {
                if (onOrderUpdated) onOrderUpdated();
                setSupervisorModalState(null);
                onClose();
            } else {
                setSupervisorModalState({
                    isOpen: true,
                    type: 'alert',
                    status: 'success',
                    title: 'Sucesso!',
                    message: 'Pedido recusado com sucesso!'
                });
                setTimeout(() => {
                    if (onOrderUpdated) onOrderUpdated();
                    setSupervisorModalState(null);
                    onClose();
                }, 1800);
            }
        } catch (error: any) {
            console.error('Erro ao processar pedido:', error);
            setSupervisorModalState({
                isOpen: true,
                type: 'alert',
                status: 'error',
                title: 'Erro',
                message: error.response?.data?.error || 'Ocorreu um erro inesperado.'
            });
        } finally {
            setIsProcessingSupervisor(false);
        }
    };

    const handleCloseOrder = async () => {
        const opFiscalSelecionada = details?.lookups?.operacoes.find((op: any) => op.ID == formData.OPERACAO_FISCAL);
        const descOperacao = opFiscalSelecionada?.DESCRIÇÃO?.toUpperCase() || '';

        if (descOperacao.includes('COTAÇÃO') || descOperacao.includes('COTACAO')) {
            setShowCotacaoAlert(true);
            return;
        }

        setShowConfirmClose(true);
    };

    const confirmCloseOrder = async () => {
        setIsClosingOrder(true);
        try {
            await closeOrder(order.ID);
            if (onOrderUpdated) onOrderUpdated();
            setFormData(prev => ({ ...prev, SITUAÇÃO: 15 }));
            setShowConfirmClose(false);
        } catch (err: any) {
            console.error(err);
            alert('Erro ao fechar pedido.');
        } finally {
            setIsClosingOrder(false);
        }
    };

    const isPedidoFechado = formData.SITUAÇÃO !== 1;
    const isFieldReadOnly = !isEditing || isPedidoFechado;
    const isGlobalReadOnly = !isEditing;
    const canEditOperacao = !isPedidoFechado; // pedido em aberto, sem depender do isEditing geral

    const handleSaveOperacao = async () => {
        if (!formData.OPERACAO) return;
        setIsSavingOperacao(true);
        try {
            await updateOrderOperacao(order.ID, Number(formData.OPERACAO));
            // Feedback visual simples sem alert
            const btn = document.getElementById('btn-save-operacao');
            if (btn) { btn.textContent = '✓ Salvo!'; setTimeout(() => { btn.textContent = 'Salvar'; }, 2000); }
        } catch (err: any) {
            console.error('Erro ao salvar operação:', err);
            alert('Erro ao salvar operação: ' + (err?.response?.data?.error || err.message));
        } finally {
            setIsSavingOperacao(false);
        }
    };

    const handleSaveObs = async () => {
        setIsSavingObs(true);
        try {
            await api.patch(`/orders/${order.ID}/observacao`, { obs: formData.OBS || '' });
            const btn = document.getElementById('btn-save-obs');
            if (btn) { btn.textContent = '✓ Salvo!'; setTimeout(() => { btn.textContent = 'Salvar Observação'; }, 2000); }
        } catch (err: any) {
            console.error('Erro ao salvar observação:', err);
            alert('Erro ao salvar observação: ' + (err?.response?.data?.error || err.message));
        } finally {
            setIsSavingObs(false);
        }
    };

    const margemReais = Number(details?.resumo?.MC_TOTAL || 0);
    const valorProdutos = Number(details?.resumo?.TotalValorProdutos || 0);

    const calculateMargemPercent = () => {
        if (valorProdutos === 0 || margemReais === 0) return 0;
        return (margemReais / valorProdutos) * 100;
    };
    const margemPercent = calculateMargemPercent();

    if (loading) {
        return (
            <div className={styles.modalOverlay}>
                <div className={styles.modalContainer}>
                    <div className={styles.highlightSection} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', paddingRight: '50px' }}>
                        <div>
                            <div className={styles.skeletonBox} style={{ width: '300px', height: '32px', marginBottom: '8px', borderRadius: '6px' }} />
                            <div className={styles.skeletonBox} style={{ width: '120px', height: '24px', borderRadius: '6px' }} />
                        </div>
                        <button onClick={onClose} style={{ position: 'absolute', top: '10px', right: '15px', fontSize: '1.8rem', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                    </div>
                    <div className={styles.contentSection}>
                        <div className={styles.modalContent}>
                            <div className={`${styles.formSection} ${styles.gridContainer} ${styles.headerGrid}`}>
                                {Array.from({ length: 7 }).map((_, i) => (
                                    <div className={styles.formGroup} key={`h-${i}`}>
                                        <div className={styles.skeletonBox} style={{ width: '60px', height: '14px', marginBottom: '4px' }} />
                                        <div className={styles.skeletonBox} style={{ width: '100%', height: '36px' }} />
                                    </div>
                                ))}
                            </div>
                            <div className={styles.tabHeader}>
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div className={styles.skeletonBox} key={`t-${i}`} style={{ width: '100px', height: '40px', marginRight: '10px', borderRadius: '6px 6px 0 0' }} />
                                ))}
                            </div>
                            <div className={styles.formSection} style={{ height: '250px' }}>
                                <div className={styles.skeletonBox} style={{ width: '100%', height: '100%' }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !details) {
        return (
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContainer}>
                    <div className={styles.errorContainer}>{error || 'Erro ao carregar.'}</div>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>
            </div>
        );
    }

    const hasOpcionais = details.items?.some((item: any) => item.OPCIONAIS && item.OPCIONAIS.trim() !== '') || false;

    return (
        <>
            <div className={styles.modalOverlay}>
                <div className={styles.modalContainer}>
                    {/* CABEÇALHO NEUTRO */}
                    <div className={styles.highlightSection} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', paddingRight: '50px' }}>
                        <div>
                            <h2 className={styles.clientName} style={{ margin: 0, marginBottom: '5px' }}>{details.pedido.CLIENTE_NOME}</h2>
                            <p className={styles.orderId} style={{ margin: 0 }}>Pedido #{details.pedido.ID}</p>
                        </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginRight: '30px', gap: '8px' }}>
                            {formData.SITUAÇÃO === 1 && (
                                <button
                                    className={styles.closeOrderButton}
                                    onClick={handleCloseOrder}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                    <CheckCircleOutlineIcon fontSize="small" /> FECHAR PEDIDO
                                </button>
                            )}

                            {/* Ações de Supervisor */}
                            {isSupervisor && details?.pedido?.SITUAÇÃO === 15 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button
                                        onClick={handleApproveOrderClick}
                                        disabled={isProcessingSupervisor}
                                        style={{
                                            backgroundColor: '#10b981',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '12px 24px',
                                            borderRadius: '8px',
                                            fontWeight: '700',
                                            cursor: isProcessingSupervisor ? 'not-allowed' : 'pointer',
                                            fontSize: '0.95rem',
                                            opacity: isProcessingSupervisor ? 0.7 : 1,
                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                                    >
                                        Aprovar
                                    </button>
                                    <button
                                        onClick={handleRejectOrderClick}
                                        disabled={isProcessingSupervisor}
                                        style={{
                                            backgroundColor: '#b91c1c',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '12px 24px',
                                            borderRadius: '8px',
                                            fontWeight: '700',
                                            cursor: isProcessingSupervisor ? 'not-allowed' : 'pointer',
                                            fontSize: '0.95rem',
                                            opacity: isProcessingSupervisor ? 0.7 : 1,
                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                                    >
                                        Recusar
                                    </button>
                                </div>
                            )}
                        </div>

                        <button onClick={onClose} style={{ position: 'absolute', top: '10px', right: '15px', fontSize: '1.8rem', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                    </div>

                    <div className={styles.contentSection}>
                        <div className={styles.modalContent}>

                            {/* --- HEADER FORM --- */}
                            <div className={`${styles.formSection} ${styles.gridContainer} ${styles.headerGrid}`}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>CÓDIGO</label>
                                    <input type="text" className={styles.formInput} value={formData?.ID || ''} readOnly />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>DESC. OPERAÇÃO</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="Digite para buscar..."
                                            value={isOpSelectOpen ? opSearchTerm : (details.lookups?.operacoes.find((op: any) => op.ID == formData?.OPERACAO_FISCAL) ? `${details.lookups?.operacoes.find((op: any) => op.ID == formData?.OPERACAO_FISCAL)?.ID} - ${details.lookups?.operacoes.find((op: any) => op.ID == formData?.OPERACAO_FISCAL)?.DESCRIÇÃO}` : '')}
                                            onChange={(e) => setOpSearchTerm(e.target.value)}
                                            onFocus={() => {
                                                setIsOpSelectOpen(true);
                                                setOpSearchTerm('');
                                            }}
                                            onBlur={() => setTimeout(() => setIsOpSelectOpen(false), 200)}
                                            disabled={!canEditOperacao}
                                            style={canEditOperacao ? { cursor: 'text', backgroundColor: 'var(--bg-widget)' } : {}}
                                        />
                                        {isOpSelectOpen && (
                                            <div style={{
                                                position: 'absolute', top: '100%', left: 0, right: 0,
                                                background: 'var(--bg-widget)', border: '1px solid var(--border-primary)', borderRadius: '6px',
                                                maxHeight: '220px', overflowY: 'auto', zIndex: 1000,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                                marginTop: '4px'
                                            }}>
                                                {details.lookups?.operacoes
                                                    .filter((op: any) =>
                                                        op.DESCRIÇÃO.toLowerCase().includes(opSearchTerm.toLowerCase()) ||
                                                        String(op.ID).includes(opSearchTerm)
                                                    )
                                                    .map((op: any) => (
                                                        <div
                                                            key={op.ID}
                                                            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '0.9rem', color: '#333', transition: 'background-color 0.1s' }}
                                                            onMouseDown={async (e) => {
                                                                e.preventDefault(); // Impede o blur de fechar antes do clique
                                                                setIsOpSelectOpen(false);

                                                                // Só atualiza se for diferente
                                                                if (formData?.OPERACAO_FISCAL != op.ID) {
                                                                    handleInputChange({ target: { name: 'OPERACAO_FISCAL', value: op.ID } } as any);
                                                                    if (canEditOperacao) {
                                                                        try {
                                                                            await updateOrderOperacao(order.ID, op.ID);
                                                                        } catch (err: any) {
                                                                            alert('Erro ao salvar operação: ' + (err?.response?.data?.error || err.message));
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f7fa'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                        >
                                                            <strong style={{ color: '#004a91' }}>{op.ID}</strong> - {op.DESCRIÇÃO}
                                                        </div>
                                                    ))
                                                }
                                                {details.lookups?.operacoes.filter((op: any) => op.DESCRIÇÃO.toLowerCase().includes(opSearchTerm.toLowerCase()) || String(op.ID).includes(opSearchTerm)).length === 0 && (
                                                    <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}>Nenhuma operação encontrada</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>PEDIDO</label>
                                    <input type="text" className={styles.formInput} name="COD_TEOREMA" value={formData?.COD_TEOREMA || ''} onChange={handleInputChange} readOnly={true} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>NOTA FISCAL</label>
                                    <input type="text" className={styles.formInput} name="DOCUMENTO" value={formData?.DOCUMENTO || ''} onChange={handleInputChange} readOnly />
                                </div>

                                {/* ================================================================= */}
                                {/* CORREÇÃO AQUI: Mapeado para EMISSAO (Data do Pedido) */}
                                {/* ================================================================= */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>EMISSÃO</label>
                                    <input
                                        type="date"
                                        className={styles.formInput}
                                        name="EMISSAO"
                                        value={formatDateForInput(formData?.EMISSAO)}
                                        onChange={handleInputChange}
                                        readOnly={isFieldReadOnly}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>PREVISÃO</label>
                                    <input
                                        type="date"
                                        className={styles.formInput}
                                        name="PREVISAO"
                                        value={formatDateForInput(formData?.PREVISAO)}
                                        onChange={handleInputChange}
                                        readOnly={isFieldReadOnly}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>SITUAÇÃO</label>
                                    <select
                                        className={styles.formSelect}
                                        name="SITUAÇÃO"
                                        value={formData?.SITUAÇÃO || 1}
                                        onChange={handleInputChange}
                                        disabled={true}
                                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                    >
                                        {details.lookups?.situacoes.map(s => (
                                            <option key={s.ID} value={s.ID}>{s.ID} - {s.DESCRIÇÃO}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* --- CLIENTE (RETRÁTIL) --- */}
                            <div className={`${styles.clientSection} ${isClientExpanded ? styles.clientSectionExpanded : ''}`}>
                                <div
                                    onClick={() => setIsClientExpanded(!isClientExpanded)}
                                    className={styles.clientHeader}
                                >
                                    <div className={styles.clientHeaderTitleContainer}>
                                        <h3 className={styles.clientHeaderTitle}>
                                            Dados do Cliente
                                        </h3>
                                        <span className={`${styles.clientStatusBadge} ${isClientExpanded ? styles.clientStatusBadgeCollapsed : styles.clientStatusBadgeExpanded}`}>
                                            {isClientExpanded ? 'Ocultar' : 'Clique para exibir'}
                                        </span>
                                    </div>
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{
                                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            transform: isClientExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            color: isClientExpanded ? '#64748b' : '#0284c7'
                                        }}
                                    >
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </div>

                                <div className={`${styles.collapsibleWrapper} ${isClientExpanded ? styles.collapsibleWrapperExpanded : ''}`}>
                                    <div className={`${styles.gridContainer} ${styles.clientGrid}`}>
                                        {/* Row 1: Identificação do Cliente */}
                                        <div className={`${styles.formGroup} ${styles.colSpan2}`}><label className={styles.formLabel}>COD. CLIENTE</label><input type="text" className={styles.formInput} value={formData?.CLIENTE || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan6}`}><label className={styles.formLabel}>CLIENTE</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_NOME || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan2}`}><label className={styles.formLabel}>CNPJ / CPF</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_CNPJ || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan2}`}><label className={styles.formLabel}>IE/RG</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_IE || ''} readOnly /></div>

                                        {/* Row 2: Contatos e CEP */}
                                        <div className={`${styles.formGroup} ${styles.colSpan3}`}><label className={styles.formLabel}>TELEFONE</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_TELEFONE || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan6}`}><label className={styles.formLabel}>EMAIL</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_EMAIL || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan3}`}><label className={styles.formLabel}>CEP</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_CEP || ''} readOnly /></div>

                                        {/* Row 3: Endereço Detalhado */}
                                        <div className={`${styles.formGroup} ${styles.colSpan5}`}><label className={styles.formLabel}>ENDEREÇO</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_ENDERECO || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan1}`}><label className={styles.formLabel}>NÚMERO</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_NUMERO || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan2}`}><label className={styles.formLabel}>BAIRRO</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_BAIRRO || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan3}`}><label className={styles.formLabel}>CIDADE</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_CIDADE || ''} readOnly /></div>
                                        <div className={`${styles.formGroup} ${styles.colSpan1}`}><label className={styles.formLabel}>UF</label><input type="text" className={styles.formInput} value={formData?.CLIENTE_UF || ''} readOnly /></div>
                                    </div>
                                </div>
                            </div>

                            {/* --- PAGAMENTO / VENDA --- */}
                            <div className={styles.formSection} style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'nowrap', paddingBottom: '20px' }}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>SEGMENTO</label>
                                    <input type="text" className={styles.formInput} value={formData?.CLIENTE_SEGMENTO_NOME || formData?.CLIENTE_SEGMENTO_ID || ''} readOnly style={{ width: 'auto', minWidth: '150px' }} />
                                </div>
                                <div className={styles.formGroup} style={{ flex: 1, minWidth: '250px' }}>
                                    <label className={styles.formLabel}>VENDEDOR</label>
                                    <input
                                        type="text"
                                        className={styles.formInput}
                                        value={formData?.VENDEDOR_NOME || formData?.VENDEDOR || ''}
                                        readOnly
                                        style={{ width: '100%', backgroundColor: '#f9f9f9', color: '#333', fontWeight: 500 }}
                                        placeholder="Nome do Vendedor"
                                    />
                                </div>
                                <div className={styles.formGroup} style={{ flex: 1, minWidth: '250px' }}>
                                    <label className={styles.formLabel}>COND. PAGAMENTO</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            name="COND_PAGAMENTO"
                                            value={formData?.COND_PAGAMENTO || ''}
                                            onChange={handleInputChange}
                                            readOnly={isFieldReadOnly}
                                            style={{ width: '70px', textAlign: 'center' }}
                                            placeholder="ID"
                                        />
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            value={formData?.COND_PAG_DESCRICAO || ''}
                                            readOnly
                                            style={{ flex: 1, backgroundColor: '#f9f9f9', fontWeight: 500, color: '#333' }}
                                            placeholder="Descrição da Condição"
                                        />
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>PRAZO M</label>
                                    <input type="number" className={styles.formInput} name="PRAZO_MEDIO" value={formData?.PRAZO_MEDIO || 0} onChange={handleInputChange} readOnly={isFieldReadOnly} style={{ width: '80px' }} />
                                </div>
                                <div className={styles.formGroup} style={{ flex: 1, minWidth: '150px' }}>
                                    <label className={styles.formLabel}>ORDEM DE COMPRA</label>
                                    <input type="text" className={styles.formInput} name="OC" value={formData?.OC || ''} onChange={handleInputChange} readOnly={isFieldReadOnly} />
                                </div>
                            </div>

                            {/* --- MENU ABAS --- */}
                            <div className={styles.formSection}>
                                <div className={styles.tabHeader}>
                                    <button className={`${styles.tabButton} ${activeTab === 'itens' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('itens')}>Itens</button>
                                    <button className={`${styles.tabButton} ${activeTab === 'entrega' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('entrega')}>Entrega</button>
                                    <button className={`${styles.tabButton} ${activeTab === 'obs' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('obs')}>Observações</button>

                                    {/*ABAS PARA ADICIONAR POSTERIOSMENTE */}
                                    <button className={`${styles.tabButton} ${activeTab === 'cotacoes_fretes' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('cotacoes_fretes')}>Cotações Fretes</button>
                                    <button className={`${styles.tabButton} ${activeTab === 'relacionamento' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('relacionamento')}>Relacionamento</button>

                                    {/*ABAS PARA ADICIONAR POSTERIOSMENTE */}
                                    {/* <button className={`${styles.tabButton} ${activeTab === 'historico' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('historico')}>Histórico</button>
                                <button className={`${styles.tabButton} ${activeTab === 'agendamento' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('agendamento')}>Agendamento</button>
                            */}
                                </div>

                                {activeTab === 'itens' && (
                                    <div className={styles.tabContent}>
                                        {!isPedidoFechado && (
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                                <button
                                                    onClick={handleOpenGradeModal}
                                                    style={{ padding: '6px 14px', borderRadius: '2px', border: '1px solid #1677ff', background: 'var(--bg-widget)', fontSize: '0.82rem', fontWeight: 600, color: '#1677ff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                >
                                                    <EditIcon fontSize="small" /> Editar Grade
                                                </button>
                                            </div>
                                        )}
                                        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '0.75rem' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid var(--border-primary)', backgroundColor: 'var(--bg-page-dashboard)', height: '40px' }}>
                                                        <th style={{ padding: '6px', textAlign: 'left', width: '40px', borderRight: '1px solid #ddd' }}>ID</th>
                                                        <th style={{ padding: '6px', textAlign: 'left', width: '60px' }}>Ref.</th>
                                                        <th style={{ padding: '6px', textAlign: 'left', minWidth: '150px' }}>Descrição</th>
                                                        {hasOpcionais && <th style={{ padding: '6px', textAlign: 'left', minWidth: '100px' }}>Opcionais</th>}
                                                        <th style={{ padding: '6px', textAlign: 'center', width: '50px' }}>Cod. PCP</th>
                                                        <th style={{ padding: '6px', textAlign: 'right', width: '50px' }}>Comissão</th>
                                                        <th style={{ padding: '6px', textAlign: 'right', width: '50px' }}>Com. IMB</th>
                                                        <th style={{ padding: '6px', textAlign: 'center', width: '40px' }}>Unid.</th>
                                                        <th style={{ padding: '6px', textAlign: 'center', width: '40px', borderRight: '1px solid #ddd' }}>Tab.</th>

                                                        {[33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48].map(size => (
                                                            <th key={size} style={{ padding: '4px 2px', textAlign: 'center', minWidth: '22px', backgroundColor: '#f1f3f5' }}>{size}</th>
                                                        ))}
                                                        <th style={{ borderRight: '1px solid #ddd', width: '5px' }}></th>

                                                        <th style={{ padding: '6px', textAlign: 'right', width: '70px' }}>Preço</th>
                                                        <th style={{ padding: '6px', textAlign: 'center', width: '50px' }}>Qtd</th>
                                                        <th style={{ padding: '6px', textAlign: 'right', width: '80px', borderRight: '1px solid #ddd' }}>Total</th>

                                                        <th style={{ padding: '6px', textAlign: 'left', minWidth: '80px' }}>Restrição</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {details.items && details.items.length > 0 ? (
                                                        details.items.map((item: any, index) => (
                                                            <tr key={index} style={{ borderBottom: '1px solid var(--border-primary)', backgroundColor: index % 2 === 0 ? 'var(--bg-widget)' : 'var(--bg-page-dashboard)' }}>
                                                                <td style={{ padding: '8px', borderRight: '1px solid #eee' }}>{item.ID}</td>
                                                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{item.REFERENCIA}</td>
                                                                <td style={{ padding: '8px' }}>{item.DESCRICAO}</td>
                                                                {hasOpcionais && <td style={{ padding: '8px', color: '#666' }}>{item.OPCIONAIS}</td>}
                                                                <td style={{ padding: '8px', textAlign: 'center' }}>{item.COD_PCP}</td>
                                                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatDecimal(item.COMISSAO, 0)}</td>
                                                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatDecimal(item.COM_IMBSEG, 0)}</td>
                                                                <td style={{ padding: '8px', textAlign: 'center' }}>{item.UNIDADE}</td>
                                                                <td style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #eee' }}>{item.TABELA}</td>

                                                                {[33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48].map(size => (
                                                                    <td key={size} style={{
                                                                        padding: '4px',
                                                                        textAlign: 'center',
                                                                        fontWeight: item[size] ? 'bold' : 'normal',
                                                                        color: item[size] ? '#000' : '#e0e0e0',
                                                                        backgroundColor: item[size] ? '#e8f5e9' : 'transparent'
                                                                    }}>
                                                                        {item[size] || '-'}
                                                                    </td>
                                                                ))}
                                                                <td style={{ borderRight: '1px solid #eee' }}></td>

                                                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.PRECO)}</td>
                                                                <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{item.QUANTIDADE}</td>
                                                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#004a91', borderRight: '1px solid #eee' }}>{formatCurrency(item.VALOR_TOTAL)}</td>

                                                                <td style={{ padding: '8px', color: 'red' }}>{item.RESTRICAO}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr><td colSpan={40} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Nenhum item encontrado.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'entrega' && (
                                    <div className={`${styles.tabContent} ${styles.entregaContainer}`}>
                                        <div className={`${styles.gridContainer} ${styles.entregaGrid}`}>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>TIPO</label><select className={styles.formSelect} disabled={isGlobalReadOnly}><option value="mesmo">O mesmo</option><option value="outro">Outro Endereço</option></select></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>NOME</label><input type="text" className={styles.formInput} name="ENTREGA_NOME" value={formData?.ENTREGA_NOME || formData?.CLIENTE_NOME || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>CNPJ</label><input type="text" className={styles.formInput} name="ENTREGA_CNPJ" value={formData?.ENTREGA_CNPJ || formData?.CLIENTE_CNPJ || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>IE</label><input type="text" className={styles.formInput} name="ENTREGA_INSCR" value={formData?.ENTREGA_INSCR || formData?.CLIENTE_IE || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                        </div>
                                        <div className={`${styles.gridContainer} ${styles.entregaGrid} ${styles.fullSpan}`} style={{ marginTop: '16px' }}>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>ENDEREÇO</label><input type="text" className={styles.formInput} name="ENTREGA_ENDERECO" value={formData?.ENTREGA_ENDERECO || formData?.CLIENTE_ENDERECO || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>NUMERO</label><input type="text" className={styles.formInput} name="ENTREGA_NUMERO" value={formData?.ENTREGA_NUMERO || formData?.CLIENTE_NUMERO || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>BAIRRO</label><input type="text" className={styles.formInput} name="ENTREGA_BAIRRO" value={formData?.ENTREGA_BAIRRO || formData?.CLIENTE_BAIRRO || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>CIDADE</label><input type="text" className={styles.formInput} name="ENTREGA_CIDADE" value={formData?.ENTREGA_CIDADE || formData?.CLIENTE_CIDADE || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>UF</label><input type="text" className={styles.formInput} name="ENTREGA_UF" value={formData?.ENTREGA_UF || formData?.CLIENTE_UF || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                        </div>
                                        <div className={styles.freteGrid}>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>FRETE</label><select className={styles.formSelect} name="FRETE_TIPO" value={formData?.FRETE_TIPO || ''} onChange={handleInputChange} disabled={isGlobalReadOnly}><option value="1">CIF</option><option value="2">FOB</option></select></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>TRANSPORTADORA</label><input type="text" className={styles.formInput} name="TRANSPORTADORA" value={formData?.TRANSPORTADORA || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                            <div className={styles.formGroup}><label className={styles.formLabel}>REDESPACHO</label><input type="text" className={styles.formInput} name="REDESPACHO" value={formData?.REDESPACHO || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} /></div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'obs' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.formGroup} style={{ width: '100%', position: 'relative' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <label className={styles.formLabel} style={{ margin: 0 }}>Observações do Pedido</label>
                                                <button 
                                                    id="btn-save-obs"
                                                    className={styles.saveButton}
                                                    onClick={handleSaveObs}
                                                    disabled={isSavingObs}
                                                    style={{
                                                        padding: '6px 16px',
                                                        fontSize: '0.85rem',
                                                        cursor: isSavingObs ? 'not-allowed' : 'pointer',
                                                        opacity: isSavingObs ? 0.7 : 1
                                                    }}
                                                >
                                                    {isSavingObs ? 'Salvando...' : 'Salvar Observação'}
                                                </button>
                                            </div>
                                            <textarea className={styles.formInput} name="OBS" value={formData.OBS || ''} onChange={handleInputChange} rows={12} style={{ fontFamily: 'inherit', lineHeight: '1.5', resize: 'vertical', minHeight: '200px' }} />
                                        </div>
                                    </div>
                                )}


                                {/* {activeTab === 'historico' && <div className={styles.tabContent}><div className={styles.formGroup} style={{ padding: '20px', textAlign: 'center', backgroundColor: '#fafafa' }}><p style={{color:'#888'}}>Histórico indisponível.</p></div></div>} */}

                                {activeTab === 'cotacoes_fretes' && (
                                    <div className={styles.tabContent}>
                                        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '0.85rem' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid var(--border-primary)', backgroundColor: 'var(--bg-page-dashboard)', height: '40px' }}>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Data</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Protocolo</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Transportadora</th>
                                                        <th style={{ padding: '8px', textAlign: 'right' }}>Vlr. Total</th>
                                                        <th style={{ padding: '8px', textAlign: 'right' }}>Vlr. Par</th>
                                                        <th style={{ padding: '8px', textAlign: 'center' }}>%</th>
                                                        <th style={{ padding: '8px', textAlign: 'center' }}>Prazo</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Usuário</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Observação</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {details.cotacoes_fretes && details.cotacoes_fretes.length > 0 ? (
                                                        details.cotacoes_fretes.map((cotacao: any, index: number) => (
                                                            <tr key={index} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                                                                <td style={{ padding: '8px' }}>{cotacao.DATA ? new Date(cotacao.DATA).toLocaleDateString() : '-'}</td>
                                                                <td style={{ padding: '8px', fontWeight: '500' }}>{cotacao.PROTOCOLO || '-'}</td>
                                                                <td style={{ padding: '8px' }}>{cotacao.NOME_FANTASIA_TRANSP || cotacao.TRANSPORTADORA || '-'}</td>
                                                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(cotacao.VALOR_COTADO)}</td>
                                                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(cotacao.VALOR_PAR)}</td>
                                                                <td style={{ padding: '8px', textAlign: 'center' }}>{cotacao.PERCENTUAL}%</td>
                                                                <td style={{ padding: '8px', textAlign: 'center' }}>{cotacao.PRAZO_PREVISTO} {cotacao.PRAZO_PREVISTO === 1 ? 'dia' : 'dias'}</td>
                                                                <td style={{ padding: '8px' }}>{cotacao.USUARIO_NOME || cotacao.USUARIO}</td>
                                                                <td style={{ padding: '8px' }}>{cotacao.OBSERVACOES || '-'}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={9} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                                                Nenhuma cotação encontrada.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'relacionamento' && (
                                    <div className={styles.tabContent}>
                                        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '0.85rem' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid var(--border-primary)', backgroundColor: 'var(--bg-page-dashboard)', height: '40px' }}>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Data</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Ação</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Cliente</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Observação</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Doc</th>
                                                        <th style={{ padding: '8px', textAlign: 'left' }}>Data Cadastro</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {details.relacionamento && details.relacionamento.length > 0 ? (
                                                        details.relacionamento.map((rel: any, index: number) => (
                                                            <tr key={index} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                                                                <td style={{ padding: '8px' }}>{rel.DATA ? new Date(rel.DATA).toLocaleDateString() : '-'}</td>
                                                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{rel.ACAO_DESC || '-'}</td>
                                                                <td style={{ padding: '8px' }}>{rel.CLIENTE_NOME || '-'}</td>
                                                                <td style={{ padding: '8px' }}>{rel.OBSERVACAO || '-'}</td>
                                                                <td style={{ padding: '8px' }}>{rel.DOC || '-'}</td>
                                                                <td style={{ padding: '8px' }}>{rel.DATA_CADASTRO ? new Date(rel.DATA_CADASTRO).toLocaleString() : '-'}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                                                Nenhum registro de relacionamento encontrado.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* 
                            {activeTab === 'agendamento' && <div className={styles.tabContent}><input type="date" className={styles.formInput} /><button className={styles.saveButton} style={{marginTop:'10px'}}>Agendar</button></div>}
                            */}



                            </div>

                        </div>
                    </div>

                    {/* --- RESUMO (FIXO NO RODAPÉ) --- */}
                    <div className={styles.resumoContainer} style={{ borderRadius: 0, border: 'none', borderTop: '1px solid #e0e0e0', margin: 0, padding: '16px 24px 24px 24px', backgroundColor: '#fafafa' }}>
                        <div className={styles.resumoItem}>
                            <p className={styles.resumoLabel}>Quant.</p>
                            <p className={styles.resumoValue}>{(details.resumo?.TotalQuant || 0).toLocaleString('pt-BR')}</p>
                        </div>
                        <div className={styles.resumoItem}>
                            <p className={styles.resumoLabel}>Valor Produtos</p>
                            <p className={styles.resumoValue}>{formatCurrency(details.resumo?.TotalValorProdutos)}</p>
                        </div>
                        <div className={styles.resumoItem}>
                            <p className={styles.resumoLabel}>Margem R$</p>
                            <p className={styles.resumoValue}>{formatCurrency(margemReais)}</p>
                        </div>
                        <div className={styles.resumoItem}>
                            <p className={styles.resumoLabel}>Margem %</p>
                            <p className={styles.resumoValue}>{isNaN(margemPercent) ? '0.00%' : `${margemPercent.toFixed(2)}%`}</p>
                        </div>
                        <div className={`${styles.formGroup} ${styles.resumoInput}`}>
                            <label className={styles.formLabel}>Correios Código de Rastreio</label>
                            <input type="text" className={styles.formInput} name="COD_RASTREIO" value={formData?.COD_RASTREIO || ''} onChange={handleInputChange} readOnly={isGlobalReadOnly} />
                        </div>
                        {currentScore !== null && simulatedScore !== null && !isNaN(currentScore) && !isNaN(simulatedScore) && formData.SITUAÇÃO === 1 && (
                            <div className={styles.resumoItem} style={{ borderLeft: '1px solid #ddd', paddingLeft: '15px', display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <p className={styles.resumoLabel} style={{ margin: 0 }}>Meta Atual</p>
                                    <p className={styles.resumoValue} style={{ fontSize: '0.9rem', color: '#64748b', margin: 0 }}>{currentScore >= 100 ? currentScore.toFixed(1) : currentScore.toFixed(2)}%</p>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <p className={styles.resumoLabel} style={{ margin: 0, color: '#0f172a' }}>Após fechar este pedido</p>
                                    <p className={styles.resumoValue} style={{ color: '#16a34a', fontWeight: 'bold', margin: 0 }}>
                                        {simulatedScore >= 100 ? simulatedScore.toFixed(1) : simulatedScore.toFixed(2)}%
                                        <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>(+{(simulatedScore - currentScore) >= 100 ? (simulatedScore - currentScore).toFixed(1) : (simulatedScore - currentScore).toFixed(2)}%)</span>
                                    </p>
                                </div>
                            </div>
                        )}
                        {/* Botões de impressão — canto inferior direito */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginLeft: 'auto' }}>
                            <button
                                onClick={() => details && openOrderPrint(details, 'internal', isImbiseg)}
                                title="Imprimir via interna (com margens)"
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#1677ff', color: '#fff', border: '1px solid #1677ff', padding: '6px 14px', borderRadius: '2px', fontWeight: '600', cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                            >
                                <PrintIcon fontSize="small" /> Interna
                            </button>
                            <button
                                onClick={() => details && openOrderPrint(details, 'client', isImbiseg)}
                                title="Imprimir via do cliente (sem margens)"
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'var(--bg-widget)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)', padding: '6px 14px', borderRadius: '2px', fontWeight: '600', cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                            >
                                <PrintIcon fontSize="small" /> Cliente
                            </button>
                        </div>
                    </div>

                    {isEditing && (
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelButton} onClick={onClose}>Cancelar</button>
                            <button className={styles.saveButton} onClick={handleSave}>Salvar Alterações</button>
                        </div>
                    )}
                </div>
            </div>

            {/* --- MODAL DE CONFIRMAÇÃO: FECHAR PEDIDO --- */}
            {showConfirmClose && (
                <div style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 10000,
                    animation: 'fadeIn 0.2s ease'
                }}>
                    <div style={{
                        background: 'var(--bg-widget)',
                        borderRadius: '4px',
                        padding: '32px 36px',
                        maxWidth: '420px',
                        width: '90%',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                        textAlign: 'center',
                        animation: 'slideUp 0.25s ease',
                        border: '1px solid var(--border-primary)'
                    }}>
                        {/* Ícone de alerta */}
                        <div style={{
                            width: '56px', height: '56px',
                            borderRadius: '4px',
                            backgroundColor: 'var(--bg-page-dashboard)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px auto',
                            fontSize: '1.8rem'
                        }}>
                            🔒
                        </div>

                        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: 700, color: '#1f1f1f' }}>
                            Fechar Pedido
                        </h3>
                        <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            Você está prestes a fechar o pedido
                        </p>
                        <p style={{ margin: '0 0 24px 0', fontSize: '1.05rem', fontWeight: 700, color: '#1677ff' }}>
                            #{order.ID} &mdash; {formData?.CLIENTE_NOME || ''}
                        </p>

                        <p style={{ margin: '0 0 24px 0', fontSize: '0.85rem', color: '#8c8c8c', background: '#fafafa', borderRadius: '2px', padding: '8px 12px', border: '1px solid #f0f0f0' }}>
                            Após o fechamento, o pedido entrará na fila de aprovação e não poderá ser alterado.
                        </p>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                onClick={() => setShowConfirmClose(false)}
                                disabled={isClosingOrder}
                                style={{
                                    flex: 1, padding: '8px 16px',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '2px', background: 'var(--bg-widget)',
                                    fontSize: '0.85rem', fontWeight: 600,
                                    color: 'var(--text-secondary)', cursor: 'pointer',
                                    transition: 'all 0.15s'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmCloseOrder}
                                disabled={isClosingOrder}
                                style={{
                                    flex: 1, padding: '8px 16px',
                                    border: '1px solid #1677ff',
                                    borderRadius: '2px',
                                    background: isClosingOrder ? '#f5f5f5' : '#1677ff',
                                    fontSize: '0.85rem', fontWeight: 600,
                                    color: isClosingOrder ? '#bfbfbf' : '#fff', cursor: isClosingOrder ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.15s'
                                }}
                            >
                                {isClosingOrder ? 'Fechando...' : 'Confirmar Fechamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL DE ALERTA: OPERAÇÃO COTAÇÃO --- */}
            {showCotacaoAlert && (
                <div style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 10000,
                    animation: 'fadeIn 0.2s ease'
                }}>
                    <div style={{
                        background: 'var(--bg-widget)',
                        borderRadius: '4px',
                        padding: '32px 36px',
                        maxWidth: '420px',
                        width: '90%',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                        textAlign: 'center',
                        animation: 'slideUp 0.25s ease',
                        border: '1px solid var(--border-primary)'
                    }}>
                        {/* Ícone de alerta (Aviso) */}
                        <div style={{
                            width: '56px', height: '56px',
                            borderRadius: '4px',
                            backgroundColor: 'var(--bg-page-dashboard)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px auto'
                        }}>
                            <WarningAmberIcon sx={{ fontSize: 28, color: '#d48806' }} />
                        </div>

                        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: 700, color: '#1f1f1f' }}>
                            Ação Bloqueada
                        </h3>
                        <p style={{ margin: '0 0 24px 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            Não é possível fechar o pedido enquanto a operação for <strong>"COTAÇÃO"</strong>.
                            Por favor, altere a <strong>OPERAÇÃO</strong> antes de prosseguir com o fechamento.
                        </p>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button
                                onClick={() => setShowCotacaoAlert(false)}
                                style={{
                                    padding: '8px 24px',
                                    border: '1px solid #1677ff',
                                    borderRadius: '2px',
                                    background: '#1677ff',
                                    fontSize: '0.85rem', fontWeight: 600,
                                    color: '#fff', cursor: 'pointer',
                                    transition: 'all 0.15s'
                                }}
                            >
                                Entendi
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* --- SUB-MODAL: ADICIONAR / EDITAR ITEM --- */}
            {showGradeModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 11000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '1600px', maxWidth: '99vw', maxHeight: '97vh', overflowY: 'auto', padding: '24px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)', border: '1px solid var(--border-primary)' }}>

                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-primary)', paddingBottom: '12px', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1f1f1f', fontSize: '1.2rem', fontWeight: 600 }}>{editingItem?.isExisting ? 'Editar Item' : 'Adicionar Item'}</h3>
                            <button onClick={() => setShowGradeModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#8c8c8c', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                        </div>

                        {/* Busca de produto (só habilitado se for novo item, item existente já tem produto fixo) */}
                        <div style={{ marginBottom: '16px' }}>
                            <label className={styles.formLabel}>Buscar Produto</label>
                            <ProductSearchInput
                                key={productSearchKey}
                                onSelect={handleProductSelectInModal}
                                segmentoId={details?.pedido?.CLIENTE_SEGMENTO_ID}
                                initialValue={editingItem?.referencia ? `${editingItem.referencia} — ${editingItem.descricao}` : ''}
                            />
                            {editingItem?.isExisting && (
                                <div style={{ fontSize: '0.8rem', color: '#8c8c8c', marginTop: '4px' }}>
                                    (Produto existente. Busque um novo produto para substituir ou clique em "Novo Item" abaixo para limpar.)
                                </div>
                            )}
                        </div>

                        {/* Formulário do Item (Ref, Descrição, Opcionais, Tabela, Preço) */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                            <div>
                                <label className={styles.formLabel}>Ref</label>
                                <input type="text" className={styles.formInput} value={editingItem?.referencia || ''} readOnly style={{ background: '#f5f5f5' }} />
                            </div>
                            <div>
                                <label className={styles.formLabel}>Descrição</label>
                                <input type="text" className={styles.formInput} value={editingItem?.descricao || ''} readOnly style={{ background: '#f5f5f5' }} />
                            </div>
                            <div>
                                <label className={styles.formLabel}>Opcionais</label>
                                <select className={styles.formSelect} value={editingItem?.opcionais || ''} onChange={e => handleEditingItemChange('opcionais', e.target.value)} disabled={!editingItem?.referencia}>
                                    <option value="">Nenhum</option>
                                    {optionsList.map((o: any) => <option key={o.ID} value={o.OPCIONAL}>{o.OPCIONAL}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={styles.formLabel}>Tabela</label>
                                <select className={styles.formSelect} value={editingItem?.tabela || ''} onChange={e => handleEditingItemChange('tabela', e.target.value)} disabled={!editingItem?.referencia}>
                                    <option value="">Selecione...</option>
                                    {tabelasList.map((t: any) => <option key={t.ID} value={t.TABELA}>{t.TABELA}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Grade de tamanhos */}
                        <div style={{ background: '#fafafa', padding: '15px', borderRadius: '4px', border: '1px solid var(--border-primary)', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                {(() => {
                                    const minS = editingItem?.grade_min != null ? Number(editingItem.grade_min) : undefined;
                                    const maxS = editingItem?.grade_max != null ? Number(editingItem.grade_max) : undefined;
                                    const hasRange = minS !== undefined && maxS !== undefined;
                                    return (
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>Subgrupo: <strong style={{ color: '#1f1f1f' }}>{editingItem?.subgrupo_nome || '-'}</strong></span>
                                            {hasRange && (
                                                <span style={{ backgroundColor: '#e6f4ff', color: '#1677ff', border: '1px solid #91caff', padding: '2px 8px', borderRadius: '2px', fontWeight: 600 }}>
                                                    Grade do Produto: {minS} ao {maxS}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '12px', alignItems: 'flex-end' }}>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    {availableSizes.map(size => (
                                        <div key={size} style={{ textAlign: 'center', minWidth: '44px' }}>
                                            <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{size}</span>
                                            <input type="text" value={editingItem?.sizes?.[size] || ''} disabled={!editingItem?.referencia}
                                                onChange={e => handleSizeChangeInModal(size, parseInt(e.target.value.replace(/\D/g, '')) || 0)}
                                                style={{ width: '100%', padding: '6px 0', textAlign: 'center', border: '1px solid var(--border-primary)', borderRadius: '2px', marginTop: '4px', fontWeight: 'bold', backgroundColor: !editingItem?.referencia ? 'var(--bg-page-dashboard)' : editingItem?.sizes?.[size] ? 'var(--bg-widget)' : 'var(--bg-widget)', cursor: !editingItem?.referencia ? 'not-allowed' : 'text' }} />
                                        </div>
                                    ))}
                                </div>
                                <div style={{ width: '1px', height: '38px', background: '#f0f0f0', margin: '0 4px' }} />
                                <div style={{ textAlign: 'center', minWidth: '65px' }}><span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>QTDE</span><input type="text" value={editingItem?.quantidade || 0} readOnly style={{ width: '100%', padding: '6px 0', textAlign: 'center', border: '1px solid var(--border-primary)', borderRadius: '2px', marginTop: '4px', fontWeight: 'bold', background: '#f5f5f5', fontSize: '1.05rem' }} /></div>
                                <div style={{ textAlign: 'center', minWidth: '110px' }}><span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>$ PREÇO</span><input type="text" value={editingItem?.preco ? (Math.trunc(editingItem.preco * 100) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'} onChange={e => { const raw = e.target.value.replace(/\D/g, ''); handleEditingItemChange('preco', raw ? Number(raw) / 100 : 0); }} disabled={!editingItem?.referencia} style={{ width: '100%', padding: '6px 8px', textAlign: 'right', border: '1px solid var(--border-primary)', borderRadius: '2px', marginTop: '4px', fontWeight: 'bold', color: '#1677ff', fontSize: '1.05rem' }} /></div>
                                <div style={{ textAlign: 'center', minWidth: '110px' }}><span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>$ TOTAL</span><input type="text" value={(Math.trunc((editingItem?.valor_total || 0) * 100) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} readOnly style={{ width: '100%', padding: '6px 8px', textAlign: 'right', border: '1px solid var(--border-primary)', borderRadius: '2px', marginTop: '4px', fontWeight: 'bold', background: '#f5f5f5', fontSize: '1.05rem' }} /></div>
                            </div>
                        </div>

                        {/* Botões do Formulário de Item */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '24px' }}>
                            <button onClick={resetEditingItem} style={{ padding: '6px 16px', borderRadius: '2px', border: '1px solid var(--border-primary)', background: 'white', color: 'var(--text-secondary)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                                Limpar / Novo Item
                            </button>
                            {editingItem?.referencia && (
                                <button onClick={handleSaveCurrentGrade} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 20px', borderRadius: '2px', border: '1px solid #1677ff', background: '#1677ff', color: 'white', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#4096ff'; e.currentTarget.style.borderColor = '#4096ff'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#1677ff'; e.currentTarget.style.borderColor = '#1677ff'; }}>
                                    ↓ Salvar esta grade
                                </button>
                            )}
                        </div>

                        {/* Lista de Itens do Pedido (Adicionados + Existentes) */}
                        {pendingItems.length > 0 && (
                            <div style={{ marginTop: '12px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>Itens do Pedido ({pendingItems.length})</div>
                                <div style={{ overflowX: 'auto', border: '1px solid var(--border-primary)', borderRadius: '4px', maxHeight: '250px', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: '#fafafa', zIndex: 5 }}>
                                            <tr>
                                                <th style={{ padding: '7px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>Status</th>
                                                <th style={{ padding: '7px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>Ref</th>
                                                <th style={{ padding: '7px', textAlign: 'left', borderBottom: '1px solid #d9d9d9', minWidth: '160px' }}>Descrição</th>
                                                {availableSizes.map(s => <th key={s} style={{ padding: '7px 3px', textAlign: 'center', borderBottom: '1px solid #d9d9d9', background: '#fafafa', minWidth: '30px' }}>{s}</th>)}
                                                <th style={{ padding: '7px', textAlign: 'center', borderBottom: '1px solid #d9d9d9' }}>Qtd</th>
                                                <th style={{ padding: '7px', textAlign: 'right', borderBottom: '1px solid #d9d9d9' }}>Total</th>
                                                <th style={{ padding: '7px', borderBottom: '1px solid #d9d9d9' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingItems.map((pi, idx) => (
                                                <tr key={pi.id} onClick={() => { setEditingItem({ ...pi }); setEditingPendingIdx(idx); setProductSearchKey(k => k + 1); }}
                                                    style={{ cursor: 'pointer', background: editingPendingIdx === idx ? 'var(--bg-page-dashboard)' : undefined }}
                                                    onMouseEnter={e => { if (editingPendingIdx !== idx) e.currentTarget.style.background = '#fafafa'; }}
                                                    onMouseLeave={e => { if (editingPendingIdx !== idx) e.currentTarget.style.background = ''; }}>
                                                    <td style={{ padding: '7px', fontWeight: 'bold', color: pi.isExisting ? '#722ed1' : '#52c41a' }}>{pi.isExisting ? 'Existente' : 'Novo'}</td>
                                                    <td style={{ padding: '7px', fontWeight: 'bold', color: '#1677ff' }}>{pi.referencia}</td>
                                                    <td style={{ padding: '7px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pi.descricao}</td>
                                                    {availableSizes.map(s => <td key={s} style={{ padding: '7px 3px', textAlign: 'center', background: pi.sizes[s] ? '#e6f4ff' : 'transparent', fontWeight: pi.sizes[s] ? 'bold' : 'normal', color: pi.sizes[s] ? '#1677ff' : '#bfbfbf' }}>{pi.sizes[s] || '-'}</td>)}
                                                    <td style={{ padding: '7px', textAlign: 'center', fontWeight: 'bold' }}>{pi.quantidade}</td>
                                                    <td style={{ padding: '7px', textAlign: 'right', fontWeight: 'bold', color: '#1677ff' }}>{pi.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                    <td style={{ padding: '7px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                        {!pi.isExisting && (
                                                            <button onClick={() => { setPendingItems(prev => prev.filter((_, i) => i !== idx)); if (editingPendingIdx === idx) resetEditingItem(); }}
                                                                style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '1.1rem', cursor: 'pointer' }}
                                                                onMouseEnter={e => e.currentTarget.style.color = '#ff4d4f'} onMouseLeave={e => e.currentTarget.style.color = '#bfbfbf'}>&times;</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Total parcial e Botão Salvar Modal */}
                        {pendingItems.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border-primary)' }}>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.72rem', color: '#8c8c8c', textTransform: 'uppercase' }}>Total Parcial</div>
                                    <strong style={{ fontSize: '1.5rem', color: '#1f1f1f' }}>{pendingItems.reduce((a, x) => a + x.valor_total, 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button className={styles.cancelButton} onClick={() => setShowGradeModal(false)} disabled={isSavingGrade}>Cancelar</button>
                            <button className={styles.saveButton} onClick={handleConfirmAllChanges} disabled={isSavingGrade}>{isSavingGrade ? 'Salvando...' : 'Confirmar Pedido'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL DE SUPERVISOR --- */}
            {supervisorModalState?.isOpen && (
                <div style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 10000,
                    animation: 'fadeIn 0.2s ease'
                }}>
                    <div style={{
                        background: 'var(--bg-widget)',
                        borderRadius: '4px',
                        padding: '32px 36px',
                        maxWidth: '420px',
                        width: '90%',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                        textAlign: 'center',
                        animation: 'slideUp 0.25s ease',
                        border: '1px solid var(--border-primary)'
                    }}>
                        {/* Ícone de alerta/sucesso/erro */}
                        <div style={{
                            width: '56px', height: '56px',
                            borderRadius: '4px',
                            backgroundColor: 'var(--bg-page-dashboard)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px auto',
                            fontSize: '1.8rem'
                        }}>
                            {supervisorModalState.status === 'success' ? '🎉' : supervisorModalState.status === 'error' ? '❌' : '⚠️'}
                        </div>

                        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: 700, color: '#1f1f1f' }}>
                            {supervisorModalState.title}
                        </h3>
                        <p style={{ margin: '0 0 24px 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            {supervisorModalState.message}
                        </p>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            {supervisorModalState.type === 'alert' ? (
                                <button
                                    onClick={() => setSupervisorModalState(null)}
                                    style={{
                                        flex: 1, padding: '8px 16px',
                                        border: '1px solid #1677ff',
                                        borderRadius: '2px',
                                        background: supervisorModalState.status === 'success' ? '#52c41a' : '#ff4d4f',
                                        fontSize: '0.85rem', fontWeight: 600,
                                        color: '#fff', cursor: 'pointer',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    OK
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setSupervisorModalState(null)}
                                        disabled={isProcessingSupervisor}
                                        style={{
                                            flex: 1, padding: '8px 16px',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '2px', background: 'var(--bg-widget)',
                                            fontSize: '0.85rem', fontWeight: 600,
                                            color: 'var(--text-secondary)', cursor: 'pointer',
                                            transition: 'all 0.15s'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={confirmSupervisorAction}
                                        disabled={isProcessingSupervisor}
                                        style={{
                                            flex: 1, padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '2px',
                                            background: supervisorModalState.type === 'approve' ? '#52c41a' : '#ff4d4f',
                                            fontSize: '0.85rem', fontWeight: 600,
                                            color: '#fff', cursor: isProcessingSupervisor ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.15s',
                                            opacity: isProcessingSupervisor ? 0.7 : 1
                                        }}
                                    >
                                        {isProcessingSupervisor ? 'Processando...' : supervisorModalState.type === 'approve' ? 'Aprovar' : 'Reprovar'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};
export default OrderDetailModal;
